// Notizen, Sprachmemos und Längen einer Station, wie das Büro sie liest
// (E3, S3). Die Memos spielen im Original (MP4 vom iPhone, WebM aus einem
// Android-Browser) direkt vom Datei-Weg des Büros – „preload=none“: Es wird
// erst geladen, wenn jemand auf Abspielen drückt, und der Server liefert in
// Stücken (Teil-Anfragen), so kann man springen. Kann der Browser ein
// Format nicht (WebM in Safari), steht das dabei, statt still zu schweigen
// (PLAN.md, Abschnitt 9: ffmpeg erst, wenn es im Büro fehlt).
//
// Nachgereichtes trägt die Marke „neu – Nachtrag vom …“; eine im Nachtrag
// berichtigte Länge sagt „berichtigt im Nachtrag vom …“ – den alten Wert
// gibt es am Server nicht (PLAN.md, E3, „Handwerk“).
//
// Seit E3b (O65) das Material von draußen je Station (und am Schluss) – in
// derselben Form wie die Längen, mit denselben Marken.

import { LAENGEN_ARTEN, type CrmAufnahme, type MaterialAmServer } from "@happy-energy/kern";
import { Ikon } from "@happy-energy/oberflaeche";
import { dateiAdresse, dauerZeigen, nachtragMarke, tagZeigen, wannZeigen, zahlZeigen } from "../anzeige.ts";

type Notiz = CrmAufnahme["notizen"][number];

/** Kann dieser Browser das Format abspielen? Unbekannt (kein Audio): ja – dann merkt man es beim Drücken. */
function kannAbspielen(format: string): boolean {
  try {
    return typeof Audio === "undefined" || new Audio().canPlayType(format) !== "";
  } catch {
    return true;
  }
}

/** Die Marke an Nachgereichtem – „neu – Nachtrag vom …“ in Lime, sonst grau. */
export function NachtragMarke({ a, nachtragId }: { a: CrmAufnahme; nachtragId: string | null }) {
  const m = nachtragMarke(nachtragId, a);
  if (!m) return null;
  return <span className={`sendungs-marke${m.neu ? "" : " alt"}`}>{m.text}</span>;
}

/** Der Text einer Notiz, so wie getippt – leer und nur eingesprochen: das steht da. */
export function NotizText({ a, notiz }: { a: CrmAufnahme; notiz: Notiz }) {
  const memos = a.memos.filter((m) => m.notizId === notiz.id).length;
  const text = notiz.text.trim();
  return (
    <>
      <NachtragMarke a={a} nachtragId={notiz.nachtragId} />
      {text ? <p className="lese-text">{notiz.text}</p> : <p className="lese-text leer">{memos > 0 ? "Nur eingesprochen – siehe Sprachmemo." : "Leer."}</p>}
    </>
  );
}

/** Die Sprachmemos einer Notiz zum Abspielen – mit Dauer und wann eingesprochen. */
export function MemoListe({ a, notizId, dunkel = false }: { a: CrmAufnahme; notizId: string; dunkel?: boolean }) {
  const memos = a.memos.filter((m) => m.notizId === notizId).sort((x, y) => x.aufgenommenAm.localeCompare(y.aufgenommenAm));
  if (memos.length === 0) return null;
  return (
    <ul className={`crm-memos${dunkel ? " dunkel" : ""}`}>
      {memos.map((m, i) => (
        <li key={m.id}>
          <span className="crm-memo-kopf">
            <Ikon name="mikro" groesse="k" /> Sprachmemo{memos.length > 1 ? ` ${i + 1}` : ""} · {dauerZeigen(m.dauerSekunden)} · eingesprochen {wannZeigen(m.aufgenommenAm)}
            <NachtragMarke a={a} nachtragId={m.nachtragId} />
          </span>
          <audio controls preload="none" src={dateiAdresse(m.pruefsumme)} aria-label={`Sprachmemo ${dauerZeigen(m.dauerSekunden)} abspielen`} />
          {!kannAbspielen(m.format) && (
            <span className="crm-memo-hinweis">Dieser Browser spielt dieses Format nicht ab ({m.format}) – bitte in Chrome oder Edge öffnen.</span>
          )}
        </li>
      ))}
    </ul>
  );
}

/** „berichtigt im Nachtrag vom …“ – an einer Länge oder einer Menge, die ein Nachtrag berichtigt hat. */
function BerichtigtMarke({ a, nachtragId }: { a: CrmAufnahme; nachtragId: string | null }) {
  if (!nachtragId) return null;
  const n = a.nachtraege.find((x) => x.id === nachtragId);
  return <span className="sendungs-marke">berichtigt im Nachtrag{n?.gesendetAm ? ` vom ${tagZeigen(n.gesendetAm)}` : ""}</span>;
}

/**
 * Das Material, das der Verkäufer draußen angegeben hat (seit E3b) – an
 * einer Station, oder mit `stationId` null: am Schluss auf der Seite
 * „Material“. Nichts da: nichts. Eine Antwort ohne die Liste (Server mit
 * altem Stand) heißt: keines.
 */
export function MaterialVonDraussen({ a, stationId, titel = "Material von draußen" }: { a: CrmAufnahme; stationId: string | null; titel?: string }) {
  const hier: MaterialAmServer[] = (a.material ?? []).filter((m) => m.stationId === stationId);
  if (hier.length === 0) return null;
  return (
    <div className="draussen">
      <div className="draussen-titel">
        <Ikon name="paket" groesse="k" /> {titel}
      </div>
      <dl className="mass-liste">
        {hier.map((m) => (
          <div key={m.id} className="mass-art">
            <dt>
              {m.bezeichnung}
              {m.artikelnummer && <span className="mass-anmerkung">Art.-Nr. {m.artikelnummer}</span>}
            </dt>
            <dd>
              <span className="mass-wert">
                <b>
                  {zahlZeigen(m.menge)} {m.einheit}
                </b>
                <NachtragMarke a={a} nachtragId={m.nachtragId} />
                <BerichtigtMarke a={a} nachtragId={m.geaendertImNachtrag} />
              </span>
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

/** Die Längen einer Station je Art (Kabelkanal, Rohr, Kabel) – mehrere einer Art einzeln und zusammen. */
export function Laengen({ a, stationId }: { a: CrmAufnahme; stationId: string }) {
  const hier = a.laengen.filter((l) => l.stationId === stationId);
  return (
    <dl className="mass-liste">
      {LAENGEN_ARTEN.map((art) => {
        const liste = hier.filter((l) => l.laengenArt === art.schluessel);
        const summe = liste.reduce((s, l) => s + l.meter, 0);
        return (
          <div key={art.schluessel} className="mass-art">
            <dt>{art.name}</dt>
            <dd>
              {liste.length === 0 && <span className="leise">–</span>}
              {liste.map((l) => (
                <span key={l.id} className="mass-wert">
                  <b>
                    {zahlZeigen(l.meter)} {art.einheit}
                  </b>
                  {l.anmerkung && <span className="mass-anmerkung">{l.anmerkung}</span>}
                  <NachtragMarke a={a} nachtragId={l.nachtragId} />
                  <BerichtigtMarke a={a} nachtragId={l.geaendertImNachtrag ?? null} />
                </span>
              ))}
              {liste.length > 1 && (
                <span className="mass-summe">
                  zusammen {zahlZeigen(summe)} {art.einheit}
                </span>
              )}
            </dd>
          </div>
        );
      })}
    </dl>
  );
}
